import React, { PropsWithChildren } from 'react';
import CheckIcon from '../../../images/check-icon.svg';
import ChevronDownIcon from '../../../images/chevron-down-icon.svg';
import ChevronUpIcon from '../../../images/cheveron-up-icon.svg';
import { useFormProvider } from '../form-provider';
import throwOnMissing from '../utils/throw-on-missing';
import FormAlert from '../form-alert';
import AiFillCaretDown from '../../../images/ai-fill-caret-down-icon.svg';
import FormLabel from '../form-label';
import usePersistedId from '../utils/use-persisted-id';
import useDynamicImports, { LoadingStrategy } from '../../../hooks/use-dynamic-imports';

export const SelectOption = ({
    children,
    value,
    disabled,
    isPlaceholder,
    Primitive,
}: PropsWithChildren<{ value: string; disabled?: boolean, isPlaceholder?: boolean, Primitive: any }>): JSX.Element => {
    return (
        <Primitive.Item
            css={{
                fontSize: '16px',
                lineHeight: 1,
                color: 'var(--color-shades-black)',
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                padding: '8px 35px 8px 25px',
                position: 'relative',
                userSelect: 'none',
                transition: 'box-shadow 0.2s',
                outline: 'none',
                boxShadow: '0 0 0 2px transparent',
                width: '100%',
                '&:focus-visible': {
                    boxShadow: '0 0 0 2px var(--color-states-focus)',
                    backgroundColor: 'var(--color-grey-300)',
                },
                '&[data-disabled]': {
                    color: 'var(--color-grey-400)',
                    pointerEvents: 'none',
                },
                '&:hover': {
                    backgroundColor: 'var(--color-grey-300)',
                },
                '@media (max-width: 767px)': {
                    fontSize: '14px',
                },
            }}
            value={value}
            disabled={disabled}
            aria-disabled={disabled}
        >
            <Primitive.ItemText>{children}</Primitive.ItemText>
            {!isPlaceholder && (
                <Primitive.ItemIndicator
                    css={{
                        // position: 'absolute',
                        // left: 0,
                        // width: 25,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CheckIcon />
                </Primitive.ItemIndicator>
            )}
        </Primitive.Item>
    );
}

export interface SelectProps
    extends Omit<
    HtmlPropsNoRef<SelectPrimitive.SelectProps>,
    'onChange' | 'onBlur'
    > {
    onChange?(value: string): void;
    onBlur?(value: boolean): void;
    value?: string;
    children: any;
    name: string;
}

const Select = ({
    children,
    onChange,
    onBlur,
    placeholder,
    name,
    required,
    disabled,
    value,
    className,
}: SelectProps) : JSX.Element => {
    throwOnMissing(name, 'name', 'Select');

    const {
        hasLoaded,
        scripts: {
            SelectPrimitive,
        },
    } = useDynamicImports({
        SelectPrimitive: () => import('@radix-ui/react-select'),
    }, LoadingStrategy.Hydrate);

    const formContext = useFormProvider();

    if (!children) {
        // eslint-disable-next-line no-param-reassign
        children = [
            <SelectOption Primitive={SelectPrimitive} isPlaceholder disabled value="NA">
                No options available.
            </SelectOption>,
        ];
    }

    if (!Array.isArray(children)) {
        // eslint-disable-next-line no-param-reassign
        children = [children];
    }

    const mappedChildren = children.map((x: any) => ({ value: x.props.value, label: x.props.children }));

    if (placeholder) {
        // eslint-disable-next-line no-param-reassign
        children = [
            <SelectOption Primitive={SelectPrimitive} isPlaceholder disabled value={placeholder}>
                {placeholder}
            </SelectOption>,
            children,
        ].flat();
    }

    const [localValue, setLocalValue] = React.useState(() => {
        if (formContext?.getValues(name) && mappedChildren.find(x => x.value === formContext.getValues(name))) {
            return mappedChildren.find(x => x.value === formContext.getValues(name));
        }
        if (!placeholder) {
            return mappedChildren[0];
        }
        return { value: null, label: placeholder };
    });

    const id = usePersistedId();

    React.useEffect(() => {
        if (!placeholder && formContext) {
            formContext.setValue(name, mappedChildren[0].label);
        }
    }, [mappedChildren, formContext, name, placeholder]);

    React.useEffect(() => {
        if (formContext && formContext.getValues(name)) {
            if (!mappedChildren.find(x => x.value === formContext.getValues(name))) {
                formContext.setValue(name, mappedChildren[0].value);
                setLocalValue(mappedChildren[0]);
            }
        }
    }, [mappedChildren, formContext, name, placeholder]);

    if (formContext) {
        formContext.register(name, { required });
    }

    // Callback version of watch.  It's your responsibility to unsubscribe when done.
    // eslint-disable-next-line consistent-return
    React.useEffect(() => {
        if (formContext) {
            const subscription = formContext.watch((value, { name: valName }) => {
                const foundOption = name === valName && value && mappedChildren.find(x => x.value === value[valName]);
                if (foundOption) {
                    setLocalValue(foundOption);
                }
            });
            return () => subscription.unsubscribe();
        }
        // eslint-disable-next-line
    }, [name, mappedChildren]);

    const RootComponent = hasLoaded ? SelectPrimitive.Root : 'div';
    const TriggerComponent = hasLoaded ? SelectPrimitive.SelectTrigger : 'div'
    const ValueComponent = hasLoaded ? SelectPrimitive.Value : 'div'
    const IconComponent = hasLoaded ? SelectPrimitive.Icon : 'div'
    const ContentComponent = hasLoaded ? SelectPrimitive.Content : 'div'
    const SelectScrollUpButton = hasLoaded ? SelectPrimitive.SelectScrollUpButton : 'div'
    const SelectScrollDownButton = hasLoaded ? SelectPrimitive.SelectScrollDownButton : 'div'
    const Viewport = hasLoaded ? SelectPrimitive.Viewport : 'div'


    return (
        <div css={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            // width: "100%"
        }}
        >
            <RootComponent
                required={required}
                onValueChange={(e) => {
                    const foundOption = mappedChildren.find(x => x.value === e);
                    setLocalValue(foundOption);
                    if (onChange) {
                        onChange(e);
                    }
                    if (formContext) {
                        formContext.setValue(name, e);
                    }
                }}
                onOpenChange={(e) => {
                    if (onBlur) {
                        onBlur(e);
                    }
                    if (formContext && !e) {
                        formContext.trigger(name);
                    }
                }}
                value={value || formContext?.getValues(name) || localValue.value}
                name={name}
            >
                <TriggerComponent
                    id={id}
                    data-value={(value || formContext?.getValues(name) || localValue.value) !== placeholder}
                    css={{
                        '&[aria-expanded="true"] ~ div label, , &[data-value="true"] ~ div label': {
                            opacity: '1',
                        },
                        '&[aria-expanded="true"], &[data-value="true"]': {
                            backgroundColor: 'transparent',
                            border: '2px solid var(--color-grey-200)',
                        },
                        border: formContext
                        && formContext.errors
                        && formContext.errors[name] ? '2px solid var(--color-states-error)' : '2px solid transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        userSelect: 'none',
                        padding: '12px',
                        width: '100%',
                        fontSize: '16px',
                        backgroundColor: 'var(--color-grey-200)',
                        color: 'var(--color-shades-black)',
                        borderRadius: '4px',
                        textAlign: 'left',
                        gap: '4px',
                        '@media (max-width: 767px)': {
                            fontSize: '16px',
                        },
                    }}
                    className={className}
                    aria-label={name}
                    disabled={disabled}
                    aria-disabled={disabled}
                >
                    <span aria-hidden="true" css={{ opacity: 0, clip: 'rect(0,0,0,0)', maxWidth: 0, maxHeight: '0' }}>
                        <ValueComponent />
                    </span>
                    <span css={{
                        width: '100%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                    >
                        {localValue.label || value || formContext?.getValues(name)}
                        <IconComponent css={{
                            paddingLeft: '8px',
                        }}
                        >
                            <AiFillCaretDown
                                style={{
                                    width: '10px',
                                    height: '10px',
                                }}
                            />
                        </IconComponent>
                    </span>
                </TriggerComponent>
                {placeholder && localValue.label !== placeholder && (
                    <FormLabel id={id}>
                        {placeholder}
                    </FormLabel>
                )}
                {hasLoaded && (
                    <ContentComponent
                        css={{
                            marginTop: 40,
                            alignSelf: 'end',
                            width: 'calc(100% - 16px)',
                            overflow: 'hidden',
                            backgroundColor: 'var(--color-grey-200)',
                            color: 'var(--color-shades-black)',
                            borderRadius: 6,
                            boxShadow: '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
                            zIndex: 300,
                        }}
                    >
                        <SelectScrollUpButton
                            css={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 25,
                                margin: '0 -1px',
                                backgroundColor: 'var(--color-grey-200)',
                                color: 'var(--color-shades-black)',
                                cursor: 'default',
                            }}
                        >
                            <ChevronUpIcon />
                        </SelectScrollUpButton>
                        <Viewport
                            css={{
                                padding: '5px',
                            }}
                        >
                            {React.Children.map(children, (r) => React.cloneElement(r, { Primitive: SelectPrimitive }))}
                        </Viewport>
                        <SelectScrollDownButton
                            css={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 25,
                                margin: '0 -1px',
                                backgroundColor: 'var(--color-grey-200)',
                                color: 'var(--color-shades-black)',
                                cursor: 'default',
                            }}
                        >
                            <ChevronDownIcon />
                        </SelectScrollDownButton>
                    </ContentComponent>
                )}
            </RootComponent>
            {formContext
                && formContext.errors
                && formContext.errors[name]
                && formContext?.errors[name]?.type === 'required' && (
                <FormAlert>Required</FormAlert>
            )}
        </div>
    );
};

export default Select;
